import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ModelService } from '../../../../core/services/model/model.service';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { LocalStorageService } from '../../../../core/services/localStorage/local-storage.service';
import { OrdersService } from '../../../../core/services/orders/orders.service';
import { AuthStateService } from '../../../../core/services/auth-state/auth-state-service.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { copyToClipboard as copyToClipboardUtil } from '../../../../core/utils/clipboard.utils';

interface AccountData {
  reference: string;
  lastname: string;
  name: string;
  type: string;
  tax_id: string;
  billing_address: string;
  billing_city: string;
  billing_state: string;
  root_account: any;
  status: boolean;
  campaign_vendor_reference:string;
}

@Component({
  selector: 'app-general-form',
  standalone: true,
  imports: [ReactiveFormsModule,
    TranslateModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    NgSelectModule,
    CommonModule],
  templateUrl: './general-form.component.html',
  styleUrl: './general-form.component.css'
})
export class GeneralFormComponent {
  @Input() campaignData?: any;
  @Input() campaignPreData?: any; // Datos de precarga para nuevas campañas
  @Input() ladata: any = [];

  @Output() emitSubmit = new EventEmitter<string>();
  account_type = this.authStateService.getAccountType();

  generalForm: FormGroup = new FormGroup({});

  draft = false;
  stage?: any;
  newStage: string = '';

  role: any;

  back = "";
  next_ok = "";
  next_ko = "";
  btn_fast_actions = false;
  agentData: AccountData[] = [];
  vendorData: AccountData[] = [];
  brokerData: AccountData[] = [];
  supplierData: AccountData[] = [];
  stagesList: { key: string }[] = [];
  supplierTypeData: any[] = [{ name: 'Prescripción broker', key: 'referral_broker' }, { name: 'Prescripción devolución', key: 'referral_vendor' }];
  supplierSellerCodes: any[] = [];

 @Output() brokerSelected = new EventEmitter<string>();

 showBrokerWarning: boolean = false;

  constructor(private fb: FormBuilder, private authStateService: AuthStateService, private modelService: ModelService, private alertService: AlertService, private localStorageService: LocalStorageService, private orderService: OrdersService) {
    this.generalForm = this.fb.group({
      campaign_reference: ['', Validators.required],
      name: ['', Validators.required],
      vendor_id: ['', Validators.required],
      broker_id: [''],
      stage: ['draft', Validators.required],
      start_date: ['', Validators.required],
      end_date: ['', Validators.required],
      vendor_end_date: ['', Validators.required],
      units: ['', Validators.required],
      delivery_comments: ['', Validators.required],
      private_comments: ['',Validators.required],
      comments: ['', Validators.required],
      campaign_vendor_reference:[""],
      campaign_stock_reference: ['', Validators.required],
      warning_label: [''],
    });
  }

  ngOnInit(): void {
      this.generalForm.get('broker_id')?.valueChanges.subscribe(brokerId => {
            this.showBrokerWarning = this.hasExcludedBrokers && !!brokerId;

            this.brokerSelected.emit(brokerId);
        });
  }

  ngAfterViewInit() {

  }
  check_selects_length() {
    if (this.brokerData.length === 1) {
      // Selecciona automáticamente la única opción
      this.generalForm.patchValue({ broker_id: this.brokerData[0].reference });
    }
    if (this.vendorData.length === 1) {
      // Selecciona automáticamente la única opción
      this.generalForm.patchValue({ vendor_id: this.vendorData[0].reference });
    }
  }

  checkUpdateForm(): boolean {
    if (this.generalForm.dirty) {
      return true;
    } else {
      return false;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if (changes['ladata'] && this.ladata.flag) {

        this.role = this.authStateService.getRole();
        this.agentData = this.ladata.agentData;
        this.vendorData = this.ladata.vendorData?.slice().sort((a: AccountData, b: AccountData) => a.name.localeCompare(b.name));
        this.brokerData = this.ladata.brokerData?.slice().sort((a: AccountData, b: AccountData) => a.name.localeCompare(b.name));
        this.supplierSellerCodes = this.ladata.supplierSellerCodes;
        this.supplierData = this.ladata.supplierData;
        this.check_selects_length();

        if (this.account_type == 'broker' && !this.draft) {
          this.generalForm.controls['broker_agent_id'].setValue(
            this.authStateService.getUserReference()
          );
        }
        if (this.campaignData) {
          this.refrescarFormulario();
        }
      }

      if (changes['campaignData']) {
        if (this.campaignData) {
          this.refrescarFormulario();
        }
      }

      // Si cambia campaignPreData y no hay campaignData (modo agregar), usar campaignPreData
      if (changes['campaignPreData']) {
        if (this.campaignPreData && !this.campaignData) {
          this.refrescarFormulario();
        }
      }
    }
  }

  changeStage(event: any) {
    this.newStage = event;
    this.btn_fast_actions = false;
  }

  refrescarFormulario() {
    this.setVariables();
    // Construir stagesList para ng-select del stage
    const dataToUseForStages = this.campaignData || this.campaignPreData;
    if (dataToUseForStages?.stages) {
      const stagesSet = new Set<string>([
        ...(dataToUseForStages.stage ? [dataToUseForStages.stage] : []),
        ...dataToUseForStages.stages,
      ]);
      this.stagesList = Array.from(stagesSet).map(k => ({ key: k }));
    }
    this.updateFieldAccessibility();
    
    // Determinar qué datos usar: campaignData para editar, campaignPreData para agregar
    const dataToUse = this.campaignData || this.campaignPreData;
    
    if (!dataToUse) return;
    
    for (const key in dataToUse) {
      const control = this.generalForm.controls[key];
      const value = dataToUse[key];
      if (control) {
        control.setValue(value);
      }
      if (control) {
        switch (key) {
          case 'broker_agent_id':
            if (this.account_type === 'broker') {
              control.setValue(value);
            } else {
              const brokerAgentName = `${dataToUse?.broker_agent?.name || ''} ${dataToUse?.broker_agent?.lastname || ''}`.trim();
              this.generalForm.get('broker_agent')?.setValue(brokerAgentName);
              control.setValue(value);
            }
            break;
          case 'supplier_agent_id':
            if (this.account_type === 'supplier') {
              control.setValue(value);
            } else {
              const supplierAgentName = `${dataToUse?.supplier_agent?.name || ''} ${dataToUse?.supplier_agent?.lastname || ''}`.trim();
              this.generalForm.get('supplier_agent')?.setValue(supplierAgentName);
              control.setValue(value);
            }
            break;
          case 'notes':
            this.generalForm.get('notes')?.setValue("");
            break;
          case 'campaign_stock_reference':
            // Siempre establecer el valor para campaign_stock_reference
            control.setValue(value);
            break;
          default:
            control.setValue(value);
            break;
        }
      }


    }
    this.btn_fast_actions = dataToUse?.next_ok || dataToUse?.next_ko ? true : false;
    this.alertService.close();
  }

  updateFieldAccessibility() {
    // Determinar qué datos usar: campaignData para editar, campaignPreData para agregar
    const dataToUse = this.campaignData || this.campaignPreData;
    const isPreData = !this.campaignData && this.campaignPreData; // Si es modo agregar con preData
    var rules = { ... dataToUse?.rules };
    
    if (isPreData) {
      // Modo agregar: Habilitar todos los campos excepto campaign_stock_reference
      for (const field in this.generalForm.controls) {
        if (this.generalForm.controls.hasOwnProperty(field)) {
          if (field === 'campaign_stock_reference') {
            this.generalForm.get(field)?.disable(); // Bloquear solo este campo
          } else {
            this.generalForm.get(field)?.enable(); // Habilitar todos los demás
          }
        }
      }
    } else {
      // Modo editar: Usar reglas de permisos
      // Deshabilitar todos los campos por defecto
      for (const field in this.generalForm.controls) {
        if (this.generalForm.controls.hasOwnProperty(field)) {
          this.generalForm.get(field)?.disable();
        }
      }

      // Habilitar solo los campos con valor 'w' en las reglas
      for (const field in rules) {
        if (rules.hasOwnProperty(field) && rules[field] === 'w') {
          this.generalForm.get(field)?.enable();
        }
      }
    }

  }

  onSubmitOrderFast(stage: string) {
    this.emitSubmit.emit(stage);
  }

  copyToClipboard(value: string, event: Event): void {
    copyToClipboardUtil(value, event);
  }

  setVariables() {
    // Determinar qué datos usar: campaignData para editar, campaignPreData para agregar
    const dataToUse = this.campaignData || this.campaignPreData;
    
    this.back = dataToUse?.back || '';
    this.next_ok = dataToUse?.next_ok;
    this.next_ko = dataToUse?.next_ko;
    this.stage = dataToUse?.stage;
    this.draft = this.campaignData ? true : false;
  }

  @Input() hasExcludedBrokers: boolean = false;
}
